import React, { forwardRef, ReactElement } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

type ButtonVariant = "primary" | "secondary" | "destructive";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  variant?: ButtonVariant;
  renderLeftIcon?: ReactElement;
  renderRightIcon?: ReactElement;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}

/** Shared across every variant: sizing rhythm, focus ring, disabled state. */
const VARIANT_BASE =
  "h-11 px-6 rounded-xl text-[15px] font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0";

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  primary:
    "text-white bg-gradient-to-r from-accent to-accent-bright border border-white/10 shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5",
  secondary:
    "text-text-primary bg-surface border border-edge hover:border-edge-strong hover:bg-overlay-hover",
  destructive:
    "text-white bg-gradient-to-r from-rose-500 to-red-600 border border-white/10 shadow-lg shadow-rose-500/20 hover:shadow-glow-sm hover:-translate-y-0.5",
};

const generateClasses = (
  disabled: boolean,
  variant: ButtonProps["variant"],
  fullWidth: boolean,
  className: string
) => {
  const baseStyles = clsx(
    "flex justify-center items-center whitespace-nowrap smallMobile:w-full",
    fullWidth ? "w-full" : "grow-0"
  );

  if (!variant) {
    return clsx(
      baseStyles,
      "text-sm leading-[17px] px-4 h-9",
      { "opacity-50": disabled },
      className
    );
  }

  return clsx(baseStyles, VARIANT_BASE, VARIANT_STYLES[variant], className);
};

const getLeftIcon = (renderLeftIcon: ButtonProps["renderLeftIcon"]) => {
  if (!renderLeftIcon) return null;
  return (
    <div className="mr-2 text-xs" data-testid="left-icon">
      {renderLeftIcon}
    </div>
  );
};

const getRightIcon = (renderRightIcon: ButtonProps["renderRightIcon"]) => {
  if (!renderRightIcon) return null;
  return (
    <div className="ml-2 text-xs" data-testid="right-icon">
      {renderRightIcon}
    </div>
  );
};

const CustomButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      loading = false,
      variant,
      disabled = false,
      children,
      renderRightIcon,
      renderLeftIcon,
      className,
      fullWidth = false,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const classes = generateClasses(disabled, variant, fullWidth, className);
    return (
      <button
        {...props}
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        data-testid="button"
        aria-disabled={disabled || loading}
      >
        {loading ? (
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        ) : (
          <>
            {getLeftIcon(renderLeftIcon)}

            <p>{children}</p>
            {getRightIcon(renderRightIcon)}
          </>
        )}
      </button>
    );
  }
);

export default CustomButton;
